/**
 * 로또 6/45 분석 라이브러리
 * 원본: 05_lotto.php / 05_lotto_numbers.js / javascript_lib.js 의 핵심 로직 포팅
 *
 * 입력 형식: rows = ["4,11,17,22,32,41", "6,13,18,28,30,36", ...]
 *           (각 row는 그 회차에서 뽑힌 6개 번호, 콤마 문자열)
 */

// ───────── 기본 유틸 ─────────
export const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function rowsToArr(rows) {
  // rows: string[] → number[][]
  return rows.map((s) => s.split(",").map((n) => Number(n)));
}

// 1-10:1, 11-20:2, 21-30:3, 31-40:4, 41-45:5
export const areaCode = (n) => {
  const v = n / 10;
  if (v <= 1) return 1;
  if (v <= 2) return 2;
  if (v <= 3) return 3;
  if (v <= 4) return 4;
  return 5;
};

export const colorClass = (n) => `ball ball-${areaCode(n)}`;

// 6개 번호 → 번호군 코드 문자열 ("112344" 등)
export const numAreaCode = (nums) =>
  nums.slice().sort((a, b) => a - b).map(areaCode).join("");

// ───────── 통계/확률 ─────────

/** 회차별 번호별 출현 회차 인덱스 (1-based)  */
export function getNumberAppearances(rows, num, weeks) {
  const arr2d = rowsToArr(rows.slice(0, weeks));
  const out = [];
  arr2d.forEach((draw, i) => {
    if (draw.includes(num)) out.push(i + 1);
  });
  return out;
}

/** 번호별 가중 확률값 = Σ(weeks - 출현회차 + 1) / weeks  */
export function getRatioArr(rows, weeks) {
  const arr = [];
  for (let n = 1; n <= 45; n++) {
    const appearances = getNumberAppearances(rows, n, weeks);
    let per = 0;
    for (const t of appearances) per += weeks - t + 1;
    arr.push(Math.floor((per / weeks) * 100) / 100);
  }
  return arr;
}

export const avg = (arr) =>
  Math.floor((arr.reduce((s, n) => s + n, 0) / arr.length) * 100) / 100;

/** 평균 이상=high, 미만=low */
export function getHighLow(rows, weeks) {
  const ratio = getRatioArr(rows, weeks);
  const a = avg(ratio);
  const high = [];
  const low = [];
  for (let i = 0; i < 45; i++) {
    if (ratio[i] >= a) high.push(i + 1);
    else low.push(i + 1);
  }
  return { high, low, ratio, avg: a };
}

/** 1차원 [번호] → 2차원 [번호군별 [번호]] */
export function groupByArea(numbers) {
  const groups = [[], [], [], [], []];
  for (const n of numbers) groups[areaCode(n) - 1].push(n);
  return groups;
}

/** 회차별 번호군 패턴 빈도 */
export function getAreaPatterns(rows, limit = 16) {
  const arr2d = rowsToArr(rows);
  const codes = arr2d.map(numAreaCode);
  const freq = {};
  for (const c of codes) freq[c] = (freq[c] || 0) + 1;
  const total = codes.length;
  return Object.entries(freq)
    .filter(([, t]) => t >= limit)
    .map(([code, t]) => [
      code,
      t,
      ((t * 100) / total).toFixed(1) + "%",
      // 마지막 출현 인덱스
      codes.lastIndexOf(code) + 1,
    ])
    .sort((a, b) => b[1] - a[1]);
}

// ───────── 번호 생성 ─────────

/**
 * 핵심 생성기: 번호군 패턴(예 "112344")을 따라 high/low에서 숫자를 뽑아 6자리 구성
 * @param {{high:number, low:number}} hl  - high/low 개수
 * @param {number[]} highNums  - high 후보 (1차원)
 * @param {number[]} lowNums   - low 후보 (1차원)
 * @param {string} pattern     - 6자리 번호군 코드 ("112344" 등)
 */
export function generateLotto({ high, low, highNums, lowNums, pattern }) {
  const hArea = groupByArea(highNums); // [[1-10], [11-20], ...]
  const lArea = groupByArea(lowNums);
  const pArr = pattern.split("");
  const result = [];

  const popRandom = (arr) => {
    if (arr.length === 0) return null;
    const idx = randInt(0, arr.length - 1);
    return arr.splice(idx, 1)[0];
  };

  // low 우선 — 패턴에서 low 개수만큼 랜덤 선택
  for (let k = 0; k < low; k++) {
    if (pArr.length === 0) break;
    const pick = pArr.splice(randInt(0, pArr.length - 1), 1)[0];
    const groupIdx = Number(pick) - 1;
    const candidate = popRandom(lArea[groupIdx]) ?? popRandom(hArea[groupIdx]);
    if (candidate != null) result.push(candidate);
  }
  // high — 남은 패턴
  while (pArr.length > 0) {
    const pick = pArr.splice(randInt(0, pArr.length - 1), 1)[0];
    const groupIdx = Number(pick) - 1;
    const candidate = popRandom(hArea[groupIdx]) ?? popRandom(lArea[groupIdx]);
    if (candidate != null) result.push(candidate);
  }
  return result.sort((a, b) => a - b);
}

/** 생성한 번호를 과거 회차와 매칭 — 0~6 매칭 회수 + 최근 4매칭 회차 */
export function simulatePrev(rows, picked) {
  const arr2d = rowsToArr(rows);
  const counts = [0, 0, 0, 0, 0, 0, 0];
  let recentFour = 0;
  arr2d.forEach((draw, i) => {
    let r = 0;
    for (const n of draw) if (picked.includes(n)) r++;
    counts[r]++;
    if (r === 4 && recentFour === 0) recentFour = arr2d.length - i;
  });
  return { counts, recentFour };
}

/** 가장 최근 회차 번호 */
export const lastDraw = (rows) =>
  rowsToArr(rows.slice(-1))[0] || [];

// ───────── 빈도 / 거리 ─────────

/** 번호별 누적 출현 횟수 [{num, count}, ...] (1~45 모두 반환) */
export function getFrequency(rows, weeks) {
  const subset = typeof weeks === "number" ? rows.slice(-weeks) : rows;
  const counts = new Array(45).fill(0);
  rowsToArr(subset).forEach((draw) => {
    for (const n of draw) counts[n - 1]++;
  });
  return counts.map((count, i) => ({ num: i + 1, count }));
}

/** 마지막 출현 후 경과 회차. (한 번도 안 나왔으면 null) */
export function getDistanceFromLast(rows) {
  const arr2d = rowsToArr(rows);
  const total = arr2d.length;
  const last = new Array(45).fill(null);
  for (let i = arr2d.length - 1; i >= 0; i--) {
    for (const n of arr2d[i]) {
      if (last[n - 1] === null) last[n - 1] = total - i - 1;
    }
  }
  return last.map((dist, i) => ({ num: i + 1, distance: dist }));
}

// ───────── 홀짝 ─────────

/** 회차별 홀짝 분포 + 전체 통계 */
export function getParityStats(rows) {
  const arr2d = rowsToArr(rows);
  const dist = new Array(7).fill(0); // dist[k] = 홀수 k개 회차 수
  let oddTotal = 0;
  let evenTotal = 0;
  arr2d.forEach((draw) => {
    let odd = 0;
    for (const n of draw) {
      if (n % 2) {
        odd++;
        oddTotal++;
      } else evenTotal++;
    }
    dist[odd]++;
  });
  const total = oddTotal + evenTotal;
  return {
    distribution: dist, // dist[0..6] = 홀수 0~6개일 때 회차 수
    oddRatio: total ? oddTotal / total : 0,
    evenRatio: total ? evenTotal / total : 0,
  };
}

// ───────── 연속 번호 ─────────

/** 회차별 연속번호 묶음 — [[연속1번호들], [연속2번호들], ...] */
export function findCascading(rows) {
  return rowsToArr(rows).map((draw) => {
    const sorted = draw.slice().sort((a, b) => a - b);
    const set = new Set();
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i] + 1 === sorted[i + 1]) {
        set.add(sorted[i]);
        set.add(sorted[i + 1]);
      }
    }
    return [...set].sort((a, b) => a - b);
  });
}

/** 번호별 "연속번호의 일부였던" 회차 수 */
export function cascadingRate(rows) {
  const out = new Array(45).fill(0);
  for (const group of findCascading(rows)) {
    for (const n of group) out[n - 1]++;
  }
  return out.map((count, i) => ({ num: i + 1, count }));
}

/** 연속번호 길이별 회차 수 (length 2~6) */
export function cascadingCount(rows) {
  const out = { 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  for (const group of findCascading(rows)) {
    if (group.length >= 2) out[Math.min(group.length, 6)]++;
  }
  return out;
}

// ───────── 연관 번호 ─────────

/** num이 들어간 회차들에서 함께 나온 다른 번호들의 빈도 (자기 자신 포함) */
export function getRelatedNumbers(rows, num) {
  const out = new Array(45).fill(0);
  rowsToArr(rows).forEach((draw) => {
    if (draw.includes(num)) {
      for (const n of draw) out[n - 1]++;
    }
  });
  return out.map((count, i) => ({ num: i + 1, count })).filter((x) => x.count > 0);
}

// ───────── 정렬 헬퍼 ─────────

/** [{num, count}] 정렬 */
export const topByCount = (arr, limit = 10) =>
  arr.slice().sort((a, b) => b.count - a.count).slice(0, limit);
export const bottomByCount = (arr, limit = 10) =>
  arr.slice().sort((a, b) => a.count - b.count).slice(0, limit);
